// features/projects/templates/continuum/cascade-verdicts.js
// ═══════════════════════════════════════════════════════════
// PHASE A VERDICTS + NEXT MOVES — Sections 5 + 6 of Cascade tab
//
// Section 5: Phase A Verdicts feed — surfaces past Phase A red-team
//   runs with three-voice breakdown (root-cause / counter-narrative /
//   outside-view). Currently 1 entry: H-3v2 SURVIVED-RESPECIFIED.
//
// Section 6: Next Moves panel — three priority anchors + Phase 7
//   wiring sequence + critical TBDs + decision points.
//
// Data: lib/cascade/phase-a-runs.js
// ═══════════════════════════════════════════════════════════

import { useState } from 'react';
import { C, FONT as F } from './tokens';
import { PHASE_A_RUNS, NEXT_MOVES } from '../../../../lib/cascade/phase-a-runs';

export default function CascadeVerdicts() {
  return (
    <>
      <PhaseAFeed />
      <NextMoves />
    </>
  );
}

// ─── Phase A Verdicts feed ───────────────────────────────────────────
function PhaseAFeed() {
  return (
    <section style={{ marginBottom: 48 }}>
      <Heading number="05" label="Phase A Verdicts" sublabel={`${PHASE_A_RUNS.length} run · three-voice red-team`} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {PHASE_A_RUNS.map((run) => (
          <PhaseACard key={run.id} run={run} />
        ))}
      </div>
    </section>
  );
}

function PhaseACard({ run }) {
  const [activeModule, setActiveModule] = useState('rootCause');
  const verdictColor = verdictColorFor(run.verdict);

  return (
    <div style={{ background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 4, overflow: 'hidden' }}>
      {/* Card header */}
      <div style={{ padding: '14px 18px', background: C.s1, borderBottom: `1px solid ${C.b1}` }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: C.gold, fontFamily: F.mono, letterSpacing: '0.06em' }}>
            {run.hypothesisId}
          </span>
          <span style={{ fontSize: 14, fontFamily: F.serif, color: C.text }}>{run.hypothesisTitle}</span>
          <span style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, marginLeft: 'auto' }}>
            Run: {run.runDate}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <VerdictBadge verdict={run.verdict} color={verdictColor} />
          <span style={{ fontSize: 11, color: C.mid, fontFamily: F.mono }}>
            {run.keyDataPoint}
          </span>
          {run.respecifiedInto && (
            <span style={{ fontSize: 11, color: C.purple, fontFamily: F.mono }}>
              → {run.respecifiedInto} (proposed)
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.55, marginTop: 10 }}>
          {run.summary}
        </div>
        {run.liveDataUpdate && <LiveDataUpdate update={run.liveDataUpdate} />}
        {run.artifact && (
          <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, marginTop: 8 }}>
            ↪ {run.artifact}
          </div>
        )}
      </div>

      {/* Three-voice modules (single-hypothesis runs) OR batch verdicts (multi-hypothesis runs) */}
      {run.modules ? (
        <>
          <ModuleTabs active={activeModule} onChange={setActiveModule} modules={run.modules} />
          <ModuleBody module={run.modules[activeModule]} kind={activeModule} />
        </>
      ) : (
        <BatchVerdictsBody run={run} />
      )}
    </div>
  );
}

function BatchVerdictsBody({ run }) {
  return (
    <div style={{ padding: '16px 20px' }}>
      {run.batchVerdicts && run.batchVerdicts.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em', marginBottom: 8 }}>
            HYPOTHESIS-LEVEL VERDICTS ({run.batchVerdicts.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
            {run.batchVerdicts.map((bv) => {
              const color = verdictColorFor(bv.verdict);
              return (
                <div key={bv.id} style={{ padding: '8px 12px', background: C.s1, border: `1px solid ${C.b2}`, borderLeft: `3px solid ${color}`, borderRadius: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11, color: C.gold, fontFamily: F.mono, letterSpacing: '0.04em' }}>{bv.id}</span>
                    <span style={{ fontSize: 9, color, fontFamily: F.mono, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '1px 6px', border: `1px solid ${color}`, borderRadius: 2 }}>
                      {bv.verdict}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.5 }}>{bv.short}</div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {run.incidentalSupport && run.incidentalSupport.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: C.sage, fontFamily: F.mono, letterSpacing: '0.06em', marginBottom: 8 }}>
            INCIDENTAL SUPPORT — other hypotheses strengthened by this run
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 16 }}>
            {run.incidentalSupport.map((s, idx) => (
              <div key={idx} style={{ padding: '6px 10px', fontSize: 11, color: C.mid, lineHeight: 1.5 }}>
                <span style={{ color: C.gold, fontFamily: F.mono, marginRight: 6 }}>{s.hypothesisId}</span>
                {s.signal}
              </div>
            ))}
          </div>
        </>
      )}

      {run.blockedNotRun && run.blockedNotRun.length > 0 && (
        <>
          <div style={{ fontSize: 11, color: C.terra, fontFamily: F.mono, letterSpacing: '0.06em', marginBottom: 8 }}>
            BLOCKED — not run in this batch
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {run.blockedNotRun.map((b, idx) => (
              <div key={idx} style={{ padding: '6px 10px', fontSize: 11, color: C.mid, lineHeight: 1.5 }}>
                <span style={{ color: C.gold, fontFamily: F.mono, marginRight: 6 }}>{b.hypothesisId}</span>
                <span style={{ color: C.dim }}>blocked on:</span> {b.blocker}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function LiveDataUpdate({ update }) {
  // Verdict color: 'inconclusive' = amber, 'survived' = sage, others = gold
  const v = update.verdict || '';
  const color = v === 'inconclusive' ? C.amber
              : v === 'survived' ? C.sage
              : v === 'killed' || v === 'rejected' ? C.terra
              : C.gold;
  return (
    <div style={{
      marginTop: 12,
      padding: '12px 14px',
      background: hexAlpha(color, 0.08),
      border: `1px solid ${color}`,
      borderLeft: `3px solid ${color}`,
      borderRadius: 3,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 9, color, fontFamily: F.mono, letterSpacing: '0.08em', padding: '2px 7px', border: `1px solid ${color}`, borderRadius: 3, background: hexAlpha(color, 0.15) }}>
          {update.revision} UPDATE
        </span>
        <span style={{ fontSize: 10, color: C.dim, fontFamily: F.mono }}>
          {update.revisionDate}
        </span>
        <span style={{ fontSize: 11, color: C.mid, fontFamily: F.mono, marginLeft: 'auto' }}>
          gap {update.gap} → <strong style={{ color, textTransform: 'uppercase' }}>{update.verdict}</strong>
        </span>
      </div>
      <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.55 }}>
        {update.interpretation}
      </div>
      {(update.patternBreakerMedian != null || update.trappedMedian != null) && (
        <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 10, color: C.static, fontFamily: F.mono, letterSpacing: '0.04em' }}>
          {update.patternBreakerMedian != null && <span>PATTERN-BREAKER MEDIAN: {update.patternBreakerMedian}</span>}
          {update.trappedMedian != null && <span>TRAPPED MEDIAN: {update.trappedMedian}</span>}
        </div>
      )}
    </div>
  );
}

function ModuleTabs({ active, onChange, modules }) {
  const items = [
    { id: 'rootCause', label: 'Root-Cause Engine' },
    { id: 'counterNarrative', label: 'Counter-Narrative' },
    { id: 'outsideView', label: 'Outside View' },
  ];
  return (
    <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${C.b1}` }}>
      {items.map((item) => {
        const isActive = active === item.id;
        return (
          <button
            key={item.id}
            onClick={() => onChange(item.id)}
            style={{
              flex: '0 0 auto',
              padding: '10px 18px',
              background: isActive ? C.s2 : 'transparent',
              border: 'none',
              borderBottom: isActive ? `2px solid ${C.gold}` : `2px solid transparent`,
              color: isActive ? C.gold : C.static,
              cursor: 'pointer',
              fontFamily: F.mono,
              fontSize: 10,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

function ModuleBody({ module, kind }) {
  if (!module) return null;
  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em', marginBottom: 4 }}>
        QUESTION
      </div>
      <div style={{ fontSize: 13, color: C.mid, fontStyle: 'italic', marginBottom: 16, lineHeight: 1.55 }}>
        {module.question}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {module.candidates.map((c, idx) => (
          <CandidateRow key={idx} candidate={c} kind={kind} />
        ))}
      </div>

      <div style={{ marginTop: 16, padding: '12px 14px', background: C.s1, border: `1px solid ${C.b2}`, borderRadius: 3 }}>
        <div style={{ fontSize: 10, color: C.gold, fontFamily: F.mono, letterSpacing: '0.06em', marginBottom: 6 }}>
          SYNTHESIS
        </div>
        <div style={{ fontSize: 12, color: C.text, lineHeight: 1.55 }}>
          {module.synthesis}
        </div>
      </div>
    </div>
  );
}

function CandidateRow({ candidate, kind }) {
  // root-cause uses `verdict` field, counter-narrative uses `strength`, outside-view uses `tilt`
  const tagText = candidate.verdict || candidate.strength || candidate.tilt || '';
  const tagColor = tagColorFor(tagText);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr', padding: '12px 14px', background: C.s1, border: `1px solid ${C.b2}`, borderRadius: 3 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12, color: C.text, fontFamily: F.sans, fontWeight: 500 }}>
          {candidate.label}
        </span>
        {tagText && (
          <span
            style={{
              fontSize: 9,
              fontFamily: F.mono,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              padding: '2px 7px',
              borderRadius: 3,
              border: `1px solid ${tagColor}`,
              color: tagColor,
            }}
          >
            {tagText}
          </span>
        )}
      </div>
      {candidate.reasoning && (
        <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.55 }}>
          {candidate.reasoning}
        </div>
      )}
      {candidate.prediction && (
        <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.55, marginBottom: 4 }}>
          <span style={{ color: C.dim, fontFamily: F.mono, fontSize: 10, marginRight: 4 }}>PREDICTION:</span>
          {candidate.prediction}
        </div>
      )}
      {candidate.implication && (
        <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.55 }}>
          <span style={{ color: C.dim, fontFamily: F.mono, fontSize: 10, marginRight: 4 }}>IMPLICATION:</span>
          {candidate.implication}
        </div>
      )}
    </div>
  );
}

// ─── Next Moves panel ────────────────────────────────────────────────
function NextMoves() {
  return (
    <section style={{ marginBottom: 48 }}>
      <Heading number="06" label="Next Moves" sublabel="priority anchors · Phase 7 wiring · critical TBDs · decisions" />

      {/* Priority anchors */}
      <SubHeading label="Three priority anchors" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 24 }}>
        {NEXT_MOVES.priorityAnchors.map((m) => (
          <PriorityAnchorRow key={m.id} move={m} />
        ))}
      </div>

      {/* Phase 7 wiring */}
      <SubHeading label="Phase 7 engineering sequence" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
        {NEXT_MOVES.phase7Wiring.map((m) => (
          <Phase7Row key={m.id} move={m} />
        ))}
      </div>

      {/* Critical TBDs */}
      {NEXT_MOVES.criticalTBDs.length > 0 && (
        <>
          <SubHeading label="Critical TBDs" warning />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
            {NEXT_MOVES.criticalTBDs.map((m) => (
              <TBDRow key={m.id} move={m} />
            ))}
          </div>
        </>
      )}

      {/* Decision points */}
      <SubHeading label="Decision points" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {NEXT_MOVES.decisionPoints.map((m) => (
          <DecisionRow key={m.id} move={m} />
        ))}
      </div>
    </section>
  );
}

function PriorityAnchorRow({ move }) {
  const isCompleted = move.status === 'completed';
  const color = isCompleted ? C.sage : C.gold;
  return (
    <div style={{ padding: '14px 18px', background: C.s2, border: `1px solid ${isCompleted ? C.sage : C.b1}`, borderLeft: `3px solid ${color}`, borderRadius: 4 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color, fontFamily: F.mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {move.label}
        </span>
        <span style={{ fontSize: 11, color: C.mid, fontFamily: F.mono }}>
          {move.hypothesisId}
        </span>
        {isCompleted && (
          <span style={{ fontSize: 9, color: C.sage, fontFamily: F.mono, letterSpacing: '0.08em', padding: '2px 7px', border: `1px solid ${C.sage}`, borderRadius: 3, background: hexAlpha(C.sage, 0.12) }}>
            COMPLETED
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: C.mid, lineHeight: 1.55 }}>
        {move.detail}
      </div>
      {move.result && (
        <div style={{ fontSize: 11, color: C.purple, fontFamily: F.mono, marginTop: 6 }}>
          {move.result}
        </div>
      )}
    </div>
  );
}

function Phase7Row({ move }) {
  return (
    <div style={{ padding: '10px 14px', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 3, display: 'grid', gridTemplateColumns: '36px 1fr 100px', gap: 14, alignItems: 'flex-start' }}>
      <span style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em' }}>
        {String(move.order).padStart(2, '0')}
      </span>
      <div>
        <div style={{ fontSize: 12, color: C.text, marginBottom: 3, display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span>{move.label}</span>
          {move.flag === 'critical-tbd' && (
            <span style={{ fontSize: 9, color: C.terra, fontFamily: F.mono, letterSpacing: '0.08em', padding: '1px 5px', border: `1px solid ${C.terra}`, borderRadius: 2 }}>
              CRITICAL TBD
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.5 }}>{move.detail}</div>
        <div style={{ fontSize: 10, color: C.dim, fontFamily: F.mono, marginTop: 4 }}>
          unblocks: {move.blocks}
        </div>
      </div>
      <span style={{ fontSize: 10, color: C.static, fontFamily: F.mono, textAlign: 'right' }}>
        {move.cost}
      </span>
    </div>
  );
}

function TBDRow({ move }) {
  return (
    <div style={{ padding: '12px 14px', background: C.s2, border: `1px solid ${C.terra}`, borderLeft: `3px solid ${C.terra}`, borderRadius: 3 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
        <span style={{ fontSize: 10, color: C.terra, fontFamily: F.mono, letterSpacing: '0.06em' }}>
          {move.hypothesisId}
        </span>
        <span style={{ fontSize: 12, color: C.text }}>{move.label}</span>
      </div>
      <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.5 }}>{move.detail}</div>
    </div>
  );
}

function DecisionRow({ move }) {
  return (
    <div style={{ padding: '10px 14px', background: C.s1, border: `1px dashed ${C.b3}`, borderRadius: 3 }}>
      <div style={{ fontSize: 12, color: C.text, marginBottom: 4 }}>{move.label}</div>
      <div style={{ fontSize: 11, color: C.mid, lineHeight: 1.5 }}>{move.detail}</div>
    </div>
  );
}

// ─── Shared atoms ────────────────────────────────────────────────────
function Heading({ number, label, sublabel }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 16 }}>
      <span style={{ fontSize: 11, color: C.dim, fontFamily: F.mono, letterSpacing: '0.06em' }}>{number}</span>
      <span style={{ fontFamily: F.serif, fontSize: 22, fontWeight: 400, color: C.text }}>{label}</span>
      {sublabel && (
        <span style={{ fontSize: 10, color: C.static, fontFamily: F.mono, letterSpacing: '0.04em', marginLeft: 6 }}>
          {sublabel}
        </span>
      )}
    </div>
  );
}

function SubHeading({ label, warning }) {
  return (
    <div style={{ fontSize: 11, color: warning ? C.terra : C.dim, fontFamily: F.mono, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8, marginTop: 4 }}>
      {label}
    </div>
  );
}

function VerdictBadge({ verdict, color }) {
  const filled = verdict === 'survived' || verdict === 'survived-respecified';
  return (
    <span
      style={{
        fontSize: 9,
        fontFamily: F.mono,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '4px 10px',
        borderRadius: 3,
        border: `1px solid ${color}`,
        background: filled ? hexAlpha(color, 0.15) : 'transparent',
        color,
        whiteSpace: 'nowrap',
        fontWeight: 500,
      }}
    >
      {verdict}
    </span>
  );
}

function verdictColorFor(verdict) {
  if (verdict === 'survived') return C.sage;
  if (verdict === 'killed' || verdict === 'rejected') return C.terra;
  if (verdict === 'survived-respecified') return C.purple;
  if (verdict === 'inconclusive') return C.amber;
  if (verdict === 'mixed') return C.gold;
  return C.static;
}

function tagColorFor(tag) {
  if (!tag) return C.static;
  const t = tag.toLowerCase();
  if (t === 'plausible' || t === 'high' || t === 'medium-high') return C.sage;
  if (t === 'partial' || t === 'medium' || t === 'partial-support') return C.gold;
  if (t === 'against') return C.terra;
  if (t === 'low') return C.dim;
  return C.mid;
}

function hexAlpha(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}
